import { useState, useRef, useEffect, useMemo } from 'react';
import { usePageTitle } from '../../../hooks/usePageTitle';
import useFitRows from '../../../hooks/useFitRows';
import {
  RotateCcw, ChevronDown, ChevronUp, BookMarked,
  TrendingUp, ArrowUpDown, BanknoteArrowDown,
  ArrowDown, Check,
  WalletCards,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, Tooltip,
} from 'recharts';
import DashboardLayout from '../../../layouts/DashboardLayout/DashboardLayout';
import StatCard from '../../../components/StatCard/StatCard';
import SearchInput from '../../../components/SearchInput/SearchInput';
import PageFooter from '../../../components/PageFooter/PageFooter';
import Pagination from '../../../components/Pagination/Pagination';
import api from '../../../services/api';
import styles from './Overview.module.css';

/* ── Static data ── */
const ACCOUNTS = [
  { id: 'all',     label: 'All accounts' },
  { id: 'fxmain',  label: 'Fx Main Line' },
  { id: 'fxone',   label: 'Fx Line One' },
  { id: 'futures', label: 'Futures Main' },
];

// Maps the account dropdown's id to the `accounts.credentials.group` value it
// filters on. "all" has no entry — it means every DFY group.
const GROUP_BY_ID = {
  fxmain:  'A-DFY',
  fxone:   'A-DFY1',
  futures: 'A-DFYF',
};

const PIE_COLORS = ['#83C35F', '#65A843', '#335126', '#3D662A', '#A6D488', '#4C7A36', '#1F3517', '#75B355'];

const money0 = (v) => `$${Math.round(Number(v) || 0).toLocaleString('en-US')}`;
const money2 = (v) =>
  `$${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Drawdown measures negative valleys only — clamp any positive result to 0.00%.
const ddPct = (v) => {
  const n = Math.min(Number(v) || 0, 0);
  return `${n.toFixed(2)}%`;
};
const moneyCompact = (v) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(2)}K`;
  return money0(n);
};
const formatM = (v) => {
  const n = (Number(v) || 0) / 1e6;
  return `${n % 1 === 0 ? n : n.toFixed(2)}M`;
};
const formatLots = (v) => {
  const n = Number(v) || 0;
  return n % 1 === 0 ? n.toString() : n.toFixed(2);
};

/* "July 10, 2026 at 08:12 AM" — matches the format this label used while it
   was a hardcoded string. */
function formatUpdated(d) {
  const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${date} at ${time}`;
}

function PieTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { name, percentage } = payload[0].payload;
  return (
    <div className={styles.pieTooltip}>
      {name}: {percentage.toFixed(2)}%
      {/* Recharts tags the wrapper with -left/-right depending on which side of
          the pointer it landed on; the caret flips to match (see the CSS). */}
      <span className={styles.pieTooltipCaret} />
    </div>
  );
}

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return mobile;
}

function usePieContainerRadius(ref) {
  const [radius, setRadius] = useState(130);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      setRadius(Math.min(130, Math.floor(w / 2) - 15));
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return radius;
}

function useIsDark() {
  const check = () => {
    const attr = document.documentElement.getAttribute('data-theme');
    return attr === 'dark'
      || (attr !== 'light' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  };
  const [dark, setDark] = useState(check);
  useEffect(() => {
    const obs = new MutationObserver(() => setDark(check()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

function Overview() {
  usePageTitle('Overview');
  const isDark = useIsDark();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab]             = useState('exposure');
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [accountOpen, setAccountOpen]         = useState(false);
  const pieContainerRef = useRef(null);
  const pieRadius = usePieContainerRadius(pieContainerRef);
  const [sortCol, setSortCol]       = useState(null);
  const [sortDir, setSortDir]       = useState('asc');
  const [summaryPage, setSummaryPage] = useState(1);
  const [loginSearch, setLoginSearch] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsError, setAccountsError] = useState(null);
  const [profitWithdrawals, setProfitWithdrawals] = useState(0);
  const [pwLoading, setPwLoading] = useState(true);
  const [aum, setAum] = useState(0);
  const [aumChart, setAumChart] = useState([]);
  const [aumLoading, setAumLoading] = useState(true);
  const [pieData, setPieData] = useState([]);
  const [pieLoading, setPieLoading] = useState(true);
  const [exposureSummary, setExposureSummary] = useState(null);
  const [exposureSummaryLoading, setExposureSummaryLoading] = useState(true);
  /* Bumped by the refresh button. Both fetch effects depend on it, so
     incrementing re-runs every request without duplicating the fetch logic. */
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  // Starting point only — the live count is measured from the window height
  // so the table ends where the screen does. See useFitRows.
  const SUMMARY_PAGE_SIZE = 12;

  /* One request per card, so "busy" is the union of their loading flags rather
     than a separate piece of state that could drift out of sync with them. */
  const busy = accountsLoading || pwLoading || aumLoading || pieLoading || exposureSummaryLoading;

  /* Stamp the timestamp when the last in-flight request settles — on first
     load, on account switch, and on every refresh. Failed requests still land
     here via their .finally, so this reads as "last attempted", not "last
     known-good". */
  useEffect(() => {
    if (!busy) setLastUpdated(new Date());
  }, [busy]);

  useEffect(() => {
    let cancelled = false;
    setAccountsLoading(true);
    setAccountsError(null);
    const group = GROUP_BY_ID[selectedAccount];
    api.get('/overview/accounts', { params: group ? { group } : {} })
      .then(({ data }) => { if (!cancelled) setAccounts(data); })
      .catch(() => { if (!cancelled) setAccountsError('Failed to load accounts'); })
      .finally(() => { if (!cancelled) setAccountsLoading(false); });
    setSummaryPage(1);
    return () => { cancelled = true; };
  }, [selectedAccount, refreshKey]);

  // Exposure summary — profit withdrawals card, AUM card + chart, exposure pie.
  useEffect(() => {
    let cancelled = false;
    const group = GROUP_BY_ID[selectedAccount];
    const params = group ? { group } : {};
    /* The exposure endpoints serve a cached mt5rest pass; the refresh button
       means the user wants a live one, so it asks for it explicitly. */
    const exposureParams = refreshKey > 0 ? { ...params, fresh: 1 } : params;

    setPwLoading(true);
    api.get('/overview/profit-withdrawals', { params })
      .then(({ data }) => { if (!cancelled) setProfitWithdrawals(data.profitWithdrawals); })
      .catch(() => { if (!cancelled) setProfitWithdrawals(0); })
      .finally(() => { if (!cancelled) setPwLoading(false); });

    setAumLoading(true);
    api.get('/overview/aum', { params })
      .then(({ data }) => { if (!cancelled) { setAum(data.aum); setAumChart(data.chart); } })
      .catch(() => { if (!cancelled) { setAum(0); setAumChart([]); } })
      .finally(() => { if (!cancelled) setAumLoading(false); });

    setPieLoading(true);
    api.get('/overview/exposure-pie', { params: exposureParams })
      .then(({ data }) => { if (!cancelled) setPieData(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setPieData([]); })
      .finally(() => { if (!cancelled) setPieLoading(false); });

    setExposureSummaryLoading(true);
    api.get('/overview/exposure-summary', { params: exposureParams })
      .then(({ data }) => { if (!cancelled) setExposureSummary(data); })
      .catch(() => { if (!cancelled) setExposureSummary(null); })
      .finally(() => { if (!cancelled) setExposureSummaryLoading(false); });

    return () => { cancelled = true; };
  }, [selectedAccount, refreshKey]);

  const sortedRows = useMemo(() => {
    const term = loginSearch.trim();
    const filtered = term
      ? accounts.filter(r => String(r.login).includes(term))
      : accounts;
    if (!sortCol) return filtered;
    return [...filtered].sort((a, b) => {
      const diff = (Number(a[sortCol]) || 0) - (Number(b[sortCol]) || 0);
      return sortDir === 'asc' ? diff : -diff;
    });
  }, [accounts, sortCol, sortDir, loginSearch]);

  const pieChartData = useMemo(
    () => pieData.map((d, i) => ({ name: d.symbol, value: d.lots, percentage: d.percentage, color: PIE_COLORS[i % PIE_COLORS.length] })),
    [pieData]
  );

  /* Mobile shows the biggest slices only — two rows' worth — behind a
     See more / See less toggle, since a long symbol list buries the card. */
  const LEGEND_COLLAPSED = 6;
  const [legendExpanded, setLegendExpanded] = useState(false);
  const legendData = useMemo(
    () => [...pieChartData].sort((a, b) => b.value - a.value),
    [pieChartData]
  );
  const legendCollapsible = isMobile && legendData.length > LEGEND_COLLAPSED;
  const legendItems = legendCollapsible && !legendExpanded
    ? legendData.slice(0, LEGEND_COLLAPSED)
    : legendData;

  /* The summary table only exists on the account tab, on desktop. */
  const summaryPaginationRef = useRef(null);
  const footerRef            = useRef(null);
  const { bodyRef: summaryBodyRef, rowsPerPage: summaryRowsPerPage } = useFitRows({
    fallback: SUMMARY_PAGE_SIZE,
    belowRefs: [summaryPaginationRef, footerRef],
    min: 6,
    max: 30,
    gutter: 24, // .content bottom padding
    enabled: activeTab === 'account' && !isMobile,
  });

  const summaryTotalPages  = Math.max(1, Math.ceil(sortedRows.length / summaryRowsPerPage));
  const summaryCurrentPage = Math.min(summaryPage, summaryTotalPages);
  const summaryPageRows    = sortedRows.slice((summaryCurrentPage - 1) * summaryRowsPerPage, summaryCurrentPage * summaryRowsPerPage);

  const accountRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target))
        setAccountOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const accountLabel = ACCOUNTS.find(a => a.id === selectedAccount)?.label ?? 'All accounts';

  return (
    <DashboardLayout>
    <div className={styles.overviewPage}>

      {/* ── Title row ── */}
      <div className={styles.titleRow}>
        <div className={styles.titleLeft}>
          <h1 className={styles.pageTitle}>Overview</h1>
          <button
            className={styles.refreshBtn}
            onClick={() => setRefreshKey(k => k + 1)}
            disabled={busy}
            title="Refresh"
            aria-label="Refresh overview data"
          >
            <RotateCcw size={20} className={busy ? styles.refreshSpin : undefined} />
          </button>
        </div>
        <span className={styles.lastUpdated}>
          {lastUpdated ? `Last updated: ${formatUpdated(lastUpdated)}` : 'Loading…'}
        </span>
      </div>

      {/* ── Tab + account selector row ── */}
      <div className={styles.tabRow}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'exposure' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('exposure')}
          >
            Exposure summary
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'account' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('account')}
          >
            Account summary
          </button>
        </div>

        {/* Account dropdown */}
        <div className={styles.accountSelector} ref={accountRef}>
          <button className={styles.accountTrigger} onClick={() => setAccountOpen(p => !p)}>
            {accountLabel.toUpperCase()}
            {accountOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>

          {accountOpen && (
            <div className={styles.accountDropdown}>
              {ACCOUNTS.map(acc => (
                <div
                  key={acc.id}
                  className={`${styles.accountOption} ${selectedAccount === acc.id ? styles.accountOptionActive : ''}`}
                  onClick={() => { setSelectedAccount(acc.id); setAccountOpen(false); }}
                >
                  <span className={styles.checkSlot}>
                    {selectedAccount === acc.id && <Check size={14} className={styles.checkIcon} />}
                  </span>
                  {acc.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Exposure summary ── */}
      {(activeTab === 'exposure' || isMobile) && (
        <div className={styles.exposureContent}>

          {/* Top row: pie chart (4/6) + AUM card (2/6) */}
          <div className="row g-3 mb-0">
            <div className="col-md-8 col-12">
              <div className={styles.card}>
                {pieLoading ? (
                  <p>Loading…</p>
                ) : pieChartData.length === 0 ? (
                  <p>No open positions</p>
                ) : (
                <div className={styles.pieWrapper}>
                  <div className={styles.pieChartContainer} ref={pieContainerRef}>
                    <ResponsiveContainer width="100%" height={isMobile ? 200 : 290}>
                      {/* accessibilityLayer off on touch: recharts activates the
                          tooltip on *focus* alone (keyboardEventsMiddleware's
                          focusAction sets keyboardInteraction at index 0), and
                          that path ignores `trigger`. A finger landing to scroll
                          focuses the chart, so the tooltip popped up unasked —
                          and left the focus ring behind. Desktop keeps it, where
                          focus really does mean keyboard. */}
                      <PieChart
                        margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                        accessibilityLayer={!isMobile}
                      >
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          outerRadius={isMobile ? 100 : pieRadius}
                          dataKey="value"
                          stroke="none"
                          /* Nothing listens for the arrow keys once the
                             accessibility layer is off, so the root shouldn't
                             hold a tab stop that does nothing but draw a ring. */
                          rootTabIndex={isMobile ? -1 : 0}
                        >
                          {pieChartData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        {/* Tap, not hover: on touch the hover trigger fires as
                            soon as a scroll starts over the chart. */}
                        <Tooltip content={<PieTooltip />} trigger={isMobile ? 'click' : 'hover'} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  <div className={styles.pieLegendWrap}>
                    <ul className={styles.pieLegend}>
                      {legendItems.map((entry, i) => (
                        <li key={entry.name ?? i} className={styles.pieLegendItem}>
                          <span className={styles.pieDot} style={{ background: entry.color }} />
                          {entry.name}
                        </li>
                      ))}
                    </ul>

                    {legendCollapsible && (
                      <button
                        type="button"
                        className={styles.legendToggle}
                        onClick={() => setLegendExpanded(v => !v)}
                      >
                        {legendExpanded ? 'See less' : 'See more'}
                        {legendExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    )}
                  </div>
                </div>
                )}
              </div>
            </div>

            <div className="col-md-4 col-12">
              <div className={`${styles.card} ${styles.aumCard}`}>
                <div className={styles.aumHeader}>
                  <span className={styles.statLabel}>ASSETS UNDER MANAGEMENT</span>
                  <div className={styles.iconBox}>
                    <WalletCards size={18} />
                  </div>
                </div>
                <div className={styles.aumValue}>{aumLoading ? '-' : moneyCompact(aum)}</div>
                <div className={styles.aumChartWrap}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={aumChart} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
                      <Area
                        type="monotone"
                        dataKey="balance"
                        stroke={isDark ? '#3D662A' : '#C9E7AF'}
                        strokeWidth={2.5}
                        fill="none"
                        dot={false}
                        isAnimationActive={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom row: 3 stat cards (each 2/6 = col-4) */}
          <div className="row g-3 mb-0">
            <div className="col-md-4 col-12">
              <StatCard label="NET VOLUME" value={exposureSummaryLoading ? '-' : formatLots(exposureSummary?.netVolume)} icon={TrendingUp} className={styles.overviewStatCard} headerClassName={styles.overviewStatCardHeader} />
            </div>
            <div className="col-md-4 col-12">
              <StatCard label="NET OPEN POSITIONS" value={exposureSummaryLoading ? '-' : formatM(exposureSummary?.totalNOP)} icon={ArrowUpDown} className={styles.overviewStatCard} headerClassName={styles.overviewStatCardHeader} />
            </div>
            <div className="col-md-4 col-12">
              <StatCard label="PROFIT WITHDRAWALS" value={pwLoading ? '-' : money0(profitWithdrawals)} icon={BanknoteArrowDown} className={styles.overviewStatCard} headerClassName={styles.overviewStatCardHeader} />
            </div>
          </div>

          {/* Limits footer row */}
          <div className={styles.limitsRow}>
            <div className={styles.limitsLeft}>
              <span>Current NOP limit: <strong>{exposureSummaryLoading ? '-' : formatM(exposureSummary?.nopLimit)}</strong></span>
              <span>Approximate lots limit: <strong>{exposureSummaryLoading ? '-' : formatLots(exposureSummary?.approxLotsLimit)}</strong></span>
            </div>
            <div className={styles.limitsRight}>
              <span>Remaining NOP capacity: <strong>{exposureSummaryLoading ? '-' : formatM(exposureSummary?.remainingNOP)}</strong></span>
              <span>Remaining lots capacity: <strong>{exposureSummaryLoading ? '-' : formatLots(exposureSummary?.remainingLots)}</strong></span>
            </div>
          </div>
        </div>
      )}

      {/* ── Account summary ── */}
      {activeTab === 'account' && !isMobile && (
        <>
          <SearchInput
            value={loginSearch}
            onChange={e => { setLoginSearch(e.target.value); setSummaryPage(1); }}
            placeholder="Search login"
            className={styles.accountSearch}
          />
        <div className={styles.tableSection}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Login</th>
                {[
                  { key: 'balance',           label: 'Balance' },
                  { key: 'equity',            label: 'Equity' },
                  { key: 'realizedProfit',    label: 'Realized profit' },
                  { key: 'drawdownAbsolute',  label: 'Drawdown (absolute)' },
                  { key: 'drawdownRelative',  label: 'Drawdown (relative)' },
                ].map(col => (
                  <th key={col.key}>
                    <button
                      className={`${styles.thBtn} ${sortCol === col.key ? styles.thBtnActive : ''}`}
                      onClick={() => {
                        if (sortCol === col.key) {
                          setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortCol(col.key);
                          setSortDir('asc');
                        }
                      }}
                    >
                      {col.label}
                      <ArrowDown
                        size={12}
                        style={{
                          transform: sortCol === col.key && sortDir === 'desc' ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.15s',
                        }}
                      />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody ref={summaryBodyRef}>
              {accountsLoading && (
                <tr><td colSpan={6}>Loading…</td></tr>
              )}
              {!accountsLoading && accountsError && (
                <tr><td colSpan={6}>{accountsError}</td></tr>
              )}
              {!accountsLoading && !accountsError && summaryPageRows.length === 0 && (
                <tr><td colSpan={6}>No accounts found</td></tr>
              )}
              {!accountsLoading && !accountsError && summaryPageRows.map(row => (
                <tr key={row.login}>
                  <td>{row.login}</td>
                  <td>{money0(row.balance)}</td>
                  <td>{money0(row.equity)}</td>
                  <td className={styles.profit}>{money2(row.realizedProfit)}</td>
                  <td className={row.drawdownAbsolute < 0 ? styles.negative : styles.profit}>
                    {ddPct(row.drawdownAbsolute)}
                  </td>
                  <td className={row.drawdownRelative < 0 ? styles.negative : styles.profit}>
                    {ddPct(row.drawdownRelative)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div ref={summaryPaginationRef}>
            <Pagination
              page={summaryCurrentPage}
              totalPages={summaryTotalPages}
              onPageChange={setSummaryPage}
            />
          </div>
        </div>
        </>
      )}

      <PageFooter ref={footerRef} />
    </div>
    </DashboardLayout>
  );
}

export default Overview;
